import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Messages() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/connexion", { state: { from: "/messagerie" } });
  }, [user, authLoading, navigate]);

  const loadConversations = useCallback(() => {
    api.get("/messages/conversations").then((r) => setConversations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) loadConversations();
  }, [user, loadConversations]);

  const openThread = (convo) => {
    setActive(convo.user);
    setLoadingThread(true);
    api
      .get(`/messages/thread/${convo.user.id}`)
      .then((r) => setThread(r.data.messages || []))
      .catch(() => {})
      .finally(() => setLoadingThread(false));
    loadConversations();
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  const send = async () => {
    if (!text.trim() || sending || !active) return;
    setSending(true);
    try {
      await api.post("/messages", { receiver_id: active.id, content: text.trim() });
      setText("");
      const r = await api.get(`/messages/thread/${active.id}`);
      setThread(r.data.messages || []);
      loadConversations();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (authLoading || !user) return <div className="pt-40 text-center text-zinc-500">Chargement…</div>;

  return (
    <div className="pt-28 pb-24 max-w-6xl mx-auto px-6 lg:px-10 min-h-screen" data-testid="messages-page">
      <p className="text-xs uppercase tracking-[0.3em] text-[#60a5fa] mb-3">Vos échanges</p>
      <h1 className="font-display text-5xl text-white tracking-tight mb-10">Messagerie</h1>

      <div className="grid md:grid-cols-3 border border-white/10 bg-[#0d0d0d] min-h-[520px]">
        {/* Conversation list */}
        <div className={`border-white/10 md:border-r ${active ? "hidden md:block" : "block"}`} data-testid="conversation-list">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              <MessageCircle className="h-8 w-8 mx-auto mb-3 text-zinc-700" />
              Aucune conversation pour l'instant. Contactez un artiste depuis une œuvre ou une enchère.
            </div>
          ) : (
            conversations.map((c) => (
              <button
                key={c.user.id}
                data-testid={`conversation-${c.user.id}`}
                onClick={() => openThread(c)}
                className={`w-full text-left p-4 border-b border-white/5 hover:bg-white/5 transition-colors flex items-center gap-3 ${
                  active?.id === c.user.id ? "bg-white/5" : ""
                }`}
              >
                <div className="h-10 w-10 shrink-0 rounded-full bg-[#2563EB]/20 flex items-center justify-center text-[#60a5fa] overflow-hidden">
                  {c.user.photo ? (
                    <img src={c.user.photo} alt="" className="h-full w-full object-cover" />
                  ) : (
                    c.user.name?.[0]?.toUpperCase() || "?"
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{c.user.name}</p>
                  <p className="text-zinc-500 text-xs truncate">{c.last_message}</p>
                </div>
                {c.unread > 0 && (
                  <span className="bg-[#E11D48] text-white text-[10px] h-5 min-w-5 px-1 flex items-center justify-center rounded-full">
                    {c.unread}
                  </span>
                )}
              </button>
            ))
          )}
        </div>

        {/* Thread */}
        <div className={`md:col-span-2 flex flex-col ${active ? "block" : "hidden md:flex"}`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
              Sélectionnez une conversation
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-white/10 flex items-center gap-3">
                <button onClick={() => setActive(null)} className="md:hidden text-zinc-400 hover:text-white">
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <p className="text-white font-display text-lg" data-testid="active-thread-name">{active.name}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="messages-thread">
                {loadingThread ? (
                  <p className="text-zinc-500 text-sm text-center mt-6">Chargement…</p>
                ) : thread.length === 0 ? (
                  <p className="text-zinc-500 text-sm text-center mt-6">Aucun message.</p>
                ) : (
                  thread.map((m) => (
                    <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[78%] px-4 py-2.5 text-sm leading-relaxed ${
                          m.mine ? "bg-[#2563EB] text-white" : "bg-white/5 text-zinc-200 border border-white/10"
                        }`}
                      >
                        {m.content}
                        <span className="block text-[10px] opacity-60 mt-1">
                          {new Date(m.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={endRef} />
              </div>
              <div className="p-4 border-t border-white/10 flex items-center gap-3">
                <input
                  data-testid="messages-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Votre message…"
                  className="flex-1 bg-black border border-white/15 px-4 py-3 text-white text-sm focus:border-[#2563EB] outline-none"
                />
                <button
                  data-testid="messages-send-button"
                  onClick={send}
                  disabled={sending || !text.trim()}
                  className="inline-flex items-center justify-center bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white h-11 w-11 transition-colors"
                  aria-label="Envoyer"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
